import { 
  Question, 
  AssessmentResponse, 
  AssessmentResult, 
  CategoryScore, 
  RiskFlag, 
  Recommendation
} from '../types/assessment';
import { demographicsQuestions } from './demographics';
import { depressionQuestions } from './depression';
import { anxietyQuestions } from './anxiety';
import { lifestyleQuestions } from './lifestyle';
import { socialSupportQuestions } from './social-support';
import { traumaStressQuestions } from './trauma-stress';
import { substanceUseDisorderQuestions } from './substance-use-disorder';

export class MentalHealthAssessmentEngine {
  private readonly allQuestions: Question[];
  private readonly version = '1.0.0';

  constructor() {
    this.allQuestions = [
      ...demographicsQuestions,
      ...depressionQuestions,
      ...anxietyQuestions,
      ...lifestyleQuestions,
      ...socialSupportQuestions,
      ...traumaStressQuestions,
      ...substanceUseDisorderQuestions
    ];
  }

  getAllQuestions(): Question[] {
    return this.allQuestions;
  }

  getQuestionsByCategory(category: string): Question[] {
    return this.allQuestions.filter(q => q.category === category);
  }

  getNextQuestion(responses: AssessmentResponse[]): Question | null {
    const answeredQuestionIds = responses.map(r => r.questionId);
    
    for (const question of this.allQuestions) {
      // Skip if already answered
      if (answeredQuestionIds.includes(question.id)) {
        continue;
      }
      
      // Check if question has conditions
      if (question.condition) {
        if (!this.evaluateCondition(question.condition, responses)) {
          continue;
        }
      }
      
      return question;
    }
    
    return null; // Assessment complete
  }

  private evaluateCondition(condition: any, responses: AssessmentResponse[]): boolean {
    const dependentResponse = responses.find(r => r.questionId === condition.dependsOn);
    
    if (!dependentResponse) {
      return false;
    }
    
    const value = dependentResponse.value;
    
    switch (condition.operator) {
      case 'equals':
        return value === condition.value;
      case 'not-equals':
        return value !== condition.value;
      case 'greater-than':
        return Number(value) > Number(condition.value);
      case 'less-than':
        return Number(value) < Number(condition.value);
      case 'includes':
        return Array.isArray(condition.value) && condition.value.includes(value);
      default:
        return false;
    }
  }

  processAssessment(userId: string, responses: AssessmentResponse[]): AssessmentResult {
    const scores = this.calculateCategoryScores(responses);
    const riskFlags = this.identifyRiskFlags(responses, scores);
    const recommendations = this.generateRecommendations(responses, scores, riskFlags);
    
    return {
      userId,
      responses,
      scores,
      riskFlags,
      recommendations,
      completedAt: new Date(),
      version: this.version
    };
  }

  private calculateCategoryScores(responses: AssessmentResponse[]): CategoryScore[] {
    const categories = ['depression', 'anxiety', 'stress', 'lifestyle', 'social-support', 'trauma', 'substance-use-disorder'];
    const scores: CategoryScore[] = [];
    
    for (const category of categories) {
      const categoryQuestions = this.getQuestionsByCategory(category);
      const categoryResponses = responses.filter(r => 
        categoryQuestions.some(q => q.id === r.questionId)
      );
      
      if (category === 'depression') {
        scores.push(this.calculatePHQ9Score(categoryResponses));
      } else if (category === 'anxiety') {
        scores.push(this.calculateGAD7Score(categoryResponses));
      } else if (category === 'substance-use-disorder') {
        scores.push(this.calculateSubstanceUseDisorderScore(categoryResponses));
      } else {
        scores.push(this.calculateGenericCategoryScore(category, categoryResponses, categoryQuestions));
      }
    }
    
    return scores;
  }

  private calculatePHQ9Score(responses: AssessmentResponse[]): CategoryScore {
    const phq9Questions = ['phq9_interest', 'phq9_mood', 'phq9_sleep', 'phq9_energy', 
                          'phq9_appetite', 'phq9_self_esteem', 'phq9_concentration', 
                          'phq9_psychomotor', 'phq9_suicidal_ideation'];
    
    let totalScore = 0;
    
    for (const questionId of phq9Questions) {
      const response = responses.find(r => r.questionId === questionId);
      if (response) {
        totalScore += Number(response.value);
      }
    }
    
    const maxScore = 27;
    const percentage = (totalScore / maxScore) * 100;
    
    let severity: 'low' | 'mild' | 'moderate' | 'severe' | 'critical';
    if (totalScore <= 4) severity = 'low';
    else if (totalScore <= 9) severity = 'mild';
    else if (totalScore <= 14) severity = 'moderate';
    else if (totalScore <= 19) severity = 'severe';
    else severity = 'critical';
    
    return {
      category: 'depression',
      subcategory: 'phq9',
      score: totalScore,
      maxScore,
      percentage,
      severity
    };
  }

  private calculateGAD7Score(responses: AssessmentResponse[]): CategoryScore {
    const gad7Questions = ['gad7_nervousness', 'gad7_worry_control', 'gad7_excessive_worry', 
                          'gad7_trouble_relaxing', 'gad7_restlessness', 'gad7_irritability', 'gad7_fear'];
    
    let totalScore = 0;
    
    for (const questionId of gad7Questions) {
      const response = responses.find(r => r.questionId === questionId);
      if (response) {
        totalScore += Number(response.value);
      }
    }
    
    const maxScore = 21;
    const percentage = (totalScore / maxScore) * 100;
    
    let severity: 'low' | 'mild' | 'moderate' | 'severe' | 'critical';
    if (totalScore <= 4) severity = 'low';
    else if (totalScore <= 9) severity = 'mild';
    else if (totalScore <= 14) severity = 'moderate';
    else severity = 'severe';
    
    return {
      category: 'anxiety',
      subcategory: 'gad7',
      score: totalScore,
      maxScore,
      percentage,
      severity
    };
  }

  private calculateSubstanceUseDisorderScore(responses: AssessmentResponse[]): CategoryScore {
    const sudQuestionIds = [
      'sud_use_past_year', 'sud_work_challenges', 'sud_relationship_impact', 
      'sud_health_changes', 'sud_reduction_consideration', 'sud_continued_despite_difficulties',
      'sud_more_than_intended', 'sud_difficulty_cutting_back', 'sud_risky_situations',
      'sud_harder_to_manage', 'sud_others_concern', 'sud_resources_consideration'
    ];
    
    let totalScore = 0;
    
    for (const questionId of sudQuestionIds) {
      const response = responses.find(r => r.questionId === questionId);
      if (response && response.value === true) {
        totalScore += 1;
      }
    }
    
    const maxScore = 12;
    const percentage = (totalScore / maxScore) * 100;
    
    let severity: 'low' | 'mild' | 'moderate' | 'severe' | 'critical';
    if (totalScore <= 2) severity = 'low';
    else if (totalScore <= 4) severity = 'mild';
    else if (totalScore <= 6) severity = 'moderate';
    else if (totalScore <= 9) severity = 'severe';
    else severity = 'critical';
    
    return {
      category: 'substance-use-disorder',
      subcategory: 'screening',
      score: totalScore,
      maxScore,
      percentage,
      severity
    };
  }

  private calculateGenericCategoryScore(category: string, responses: AssessmentResponse[], questions: Question[]): CategoryScore {
    let totalScore = 0;
    let maxScore = 0;
    
    for (const question of questions) {
      const response = responses.find(r => r.questionId === question.id);
      if (response && question.options) {
        if (question.type === 'multi-select' && Array.isArray(response.value)) {
          // For multi-select questions, count each selected option
          for (const selectedValue of response.value) {
            const option = question.options.find(opt => opt.value === selectedValue);
            if (option && option.score !== undefined) {
              totalScore += option.score;
            }
          }
          // Max score for multi-select is sum of all positive scores
          const positiveScores = question.options.filter(opt => opt.score && opt.score > 0);
          maxScore += positiveScores.reduce((sum, opt) => sum + (opt.score || 0), 0);
        } else {
          // For single-choice questions
          const option = question.options.find(opt => opt.value === response.value);
          if (option && option.score !== undefined) {
            totalScore += option.score;
          }
          const maxQuestionScore = Math.max(...question.options.map(opt => opt.score || 0));
          maxScore += maxQuestionScore;
        }
      }
    }
    
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    
    let severity: 'low' | 'mild' | 'moderate' | 'severe' | 'critical';
    if (category === 'trauma') {
      // Special scoring for trauma - based on trauma impact and PTSD symptoms
      const traumaImpactResponse = responses.find(r => r.questionId === 'trauma_impact');
      const ptsdSymptoms = ['ptsd_nightmares', 'ptsd_avoidance', 'ptsd_hypervigilance', 'ptsd_numbness', 'ptsd_guilt']
        .filter(qId => {
          const response = responses.find(r => r.questionId === qId);
          return response && response.value === true;
        }).length;
        
      if (traumaImpactResponse) {
        const impactScore = Number(traumaImpactResponse.value === 'severe-impact' ? 4 : 
                                  traumaImpactResponse.value === 'significant-impact' ? 3 :
                                  traumaImpactResponse.value === 'moderate-impact' ? 2 :
                                  traumaImpactResponse.value === 'minimal-impact' ? 1 : 0);
        totalScore = Math.max(totalScore, impactScore + ptsdSymptoms);
        maxScore = Math.max(maxScore, 9); // 4 for impact + 5 for PTSD symptoms
      }
      
      if (totalScore <= 1) severity = 'low';
      else if (totalScore <= 3) severity = 'mild';
      else if (totalScore <= 5) severity = 'moderate';
      else if (totalScore <= 7) severity = 'severe';
      else severity = 'critical';
    } else {
      // Standard percentage-based scoring for other categories
      if (percentage <= 20) severity = 'low';
      else if (percentage <= 40) severity = 'mild';
      else if (percentage <= 60) severity = 'moderate';
      else if (percentage <= 80) severity = 'severe';
      else severity = 'critical';
    }
    
    return {
      category,
      score: totalScore,
      maxScore,
      percentage,
      severity
    };
  }

  private identifyRiskFlags(responses: AssessmentResponse[], scores: CategoryScore[]): RiskFlag[] {
    const flags: RiskFlag[] = [];
    
    // Suicide risk assessment
    const suicidalIdeation = responses.find(r => r.questionId === 'phq9_suicidal_ideation');
    if (suicidalIdeation && Number(suicidalIdeation.value) > 0) {
      const severity = Number(suicidalIdeation.value) === 1 ? 'low' : 
                      Number(suicidalIdeation.value) === 2 ? 'moderate' : 'high';
      
      flags.push({
        type: 'suicide',
        severity: severity as 'low' | 'moderate' | 'high',
        triggeredBy: ['phq9_suicidal_ideation'],
        description: 'Reported thoughts of self-harm or suicide',
        immediateAction: severity === 'high' ? 
          'Immediate professional intervention recommended. Contact crisis hotline or emergency services.' :
          'Professional assessment recommended within 24-48 hours.'
      });
    }
    
    // Severe depression
    const depressionScore = scores.find(s => s.category === 'depression');
    if (depressionScore && depressionScore.severity === 'critical') {
      flags.push({
        type: 'severe-depression',
        severity: 'critical',
        triggeredBy: ['depression_assessment'],
        description: 'Severe depression symptoms detected'
      });
    }
    
    // Substance use disorder risk
    const sudScore = scores.find(s => s.category === 'substance-use-disorder');
    if (sudScore && sudScore.severity !== 'low') {
      const severity = sudScore.severity === 'critical' || sudScore.severity === 'severe' ? 'high' : 'moderate';
      flags.push({
        type: 'substance-abuse',
        severity: severity as 'low' | 'moderate' | 'high',
        triggeredBy: ['substance_use_disorder_assessment'],
        description: `Substance use disorder screening indicates ${sudScore.severity} level of concern`,
        immediateAction: severity === 'high' ? 
          'Professional substance use assessment recommended.' :
          'Consider discussing substance use with a healthcare provider.'
      });
    }
    
    // PTSD screening
    const ptsdQuestions = ['ptsd_nightmares', 'ptsd_avoidance', 'ptsd_hypervigilance', 'ptsd_numbness', 'ptsd_guilt'];
    const ptsdSymptoms = ptsdQuestions.filter(qId => {
      const response = responses.find(r => r.questionId === qId);
      return response && response.value === true;
    }).length;
    
    if (ptsdSymptoms >= 3) {
      flags.push({
        type: 'trauma',
        severity: ptsdSymptoms >= 4 ? 'high' : 'moderate',
        triggeredBy: ptsdQuestions,
        description: 'Possible PTSD symptoms detected'
      });
    }
    
    return flags;
  }

  private generateRecommendations(responses: AssessmentResponse[], scores: CategoryScore[], riskFlags: RiskFlag[]): Recommendation[] {
    const recommendations: Recommendation[] = [];
    
    // High priority - Crisis intervention
    if (riskFlags.some(flag => flag.type === 'suicide')) {
      recommendations.push({
        category: 'crisis',
        priority: 'urgent',
        type: 'emergency',
        title: 'Immediate Support Needed',
        description: 'Your responses indicate you may be having thoughts of self-harm. Please reach out for immediate support.',
        resources: [
          {
            title: 'National Suicide Prevention Lifeline',
            type: 'helpline',
            description: '24/7 crisis support - Call 988',
            url: 'tel:988'
          },
          {
            title: 'Crisis Text Line',
            type: 'helpline',
            description: 'Text HOME to 741741 for 24/7 crisis support'
          }
        ]
      });
    }
    
    // Depression recommendations
    const depressionScore = scores.find(s => s.category === 'depression');
    if (depressionScore && depressionScore.severity !== 'low') {
      const priority = depressionScore.severity === 'critical' ? 'urgent' : 
                      depressionScore.severity === 'severe' ? 'high' : 'medium';
      
      recommendations.push({
        category: 'depression',
        priority: priority as 'medium' | 'high' | 'urgent',
        type: 'therapy',
        title: 'Depression Support',
        description: 'Consider professional support for depression symptoms',
        resources: [
          {
            title: 'Find a Therapist',
            type: 'professional',
            description: 'Psychology Today therapist directory',
            url: 'https://www.psychologytoday.com'
          },
          {
            title: 'Cognitive Behavioral Therapy (CBT)',
            type: 'article',
            description: 'Evidence-based treatment for depression'
          }
        ]
      });
    }
    
    // Anxiety recommendations
    const anxietyScore = scores.find(s => s.category === 'anxiety');
    if (anxietyScore && anxietyScore.severity !== 'low') {
      recommendations.push({
        category: 'anxiety',
        priority: anxietyScore.severity === 'severe' ? 'high' : 'medium',
        type: 'self-care',
        title: 'Anxiety Management',
        description: 'Techniques and resources for managing anxiety',
        resources: [
          {
            title: 'Mindfulness Meditation',
            type: 'app',
            description: 'Apps like Headspace or Calm can help with anxiety'
          },
          {
            title: 'Progressive Muscle Relaxation',
            type: 'article',
            description: 'Technique for reducing physical tension from anxiety'
          }
        ]
      });
    }
    
    // Lifestyle recommendations
    const sleepProblems = responses.find(r => r.questionId === 'sleep_problems');
    if (sleepProblems && Array.isArray(sleepProblems.value) && sleepProblems.value.length > 0) {
      recommendations.push({
        category: 'lifestyle',
        priority: 'medium',
        type: 'lifestyle',
        title: 'Sleep Hygiene',
        description: 'Improve your sleep quality with better sleep habits',
        resources: [
          {
            title: 'Sleep Foundation Guidelines',
            type: 'article',
            description: 'Evidence-based sleep hygiene recommendations',
            url: 'https://www.sleepfoundation.org'
          }
        ]
      });
    }
    
    // Social support recommendations
    const socialScore = scores.find(s => s.category === 'social-support');
    if (socialScore && socialScore.severity === 'low') {
      recommendations.push({
        category: 'social-support',
        priority: 'medium',
        type: 'lifestyle',
        title: 'Building Social Connections',
        description: 'Strategies for developing meaningful relationships and support systems',
        resources: [
          {
            title: 'Meetup Groups',
            type: 'app',
            description: 'Find local groups with shared interests',
            url: 'https://www.meetup.com'
          },
          {
            title: 'Volunteer Opportunities',
            type: 'article',
            description: 'Volunteering as a way to meet people and give back'
          }
        ]
      });
    }
    
    return recommendations;
  }
}

export const assessmentEngine = new MentalHealthAssessmentEngine(); 